import React from 'react';
import { cn } from '@/lib/utils';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CircularProgressProps {
  value: number;
  max: number;
  size?: number;
  strokeWidth?: number;
  label: string;
  unit: string;
  className?: string;
  icon?: React.ReactNode;
  gradientId?: string;
  gradientColors?: { start: string; end: string };
  glowColor?: string;
  infoContent?: React.ReactNode;
}

export const CircularProgress = ({
  value,
  max,
  size = 120,
  strokeWidth = 8,
  label,
  unit,
  className,
  icon,
  gradientId = 'progress-gradient',
  gradientColors,
  glowColor,
  infoContent
}: CircularProgressProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = Math.min((value / max) * 100, 100);
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
          style={{
            filter: glowColor 
              ? `drop-shadow(0 0 10px ${glowColor}80) drop-shadow(0 0 25px ${glowColor}40)` 
              : 'drop-shadow(0 0 8px hsl(157 100% 50% / 0.5)) drop-shadow(0 0 20px hsl(157 100% 50% / 0.25))'
          }}
        >
          {gradientColors && (
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={gradientColors.start} />
                <stop offset="100%" stopColor={gradientColors.end} />
              </linearGradient>
            </defs>
          )}
          {/* Background circle - semi-transparent white */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth={strokeWidth}
          />
          {/* Soft inner glow */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={gradientColors ? `url(#${gradientId})` : "hsl(var(--primary))"}
            strokeWidth={strokeWidth + 4}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            opacity={0.15}
            className="transition-all duration-500 ease-out"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={gradientColors ? `url(#${gradientId})` : "hsl(var(--primary))"}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
          />
        </svg>
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {icon && <div className="mb-1 text-primary">{icon}</div>}
          <div className="text-2xl font-bold text-foreground" style={{ textShadow: glowColor ? `0 0 12px ${glowColor}40` : undefined }}>
            {Math.round(value)}
          </div>
          <div className="text-xs text-muted-foreground">
            / {Math.round(max)}{unit}
          </div>
        </div>
      </div>
      <div className="text-sm font-medium text-center flex items-center gap-1.5 justify-center">
        {label}
        {infoContent && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs p-3">
                {infoContent}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};
